(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const heroes = new Map();
  const canObserve = 'IntersectionObserver' in window;

  function isInViewport(node) {
    const rect = node.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 &&
      rect.top < window.innerHeight && rect.left < window.innerWidth;
  }

  function initHero(root) {
    if (!root || root.dataset.rpHomeReady === 'true') return;
    root.dataset.rpHomeReady = 'true';

    const counts = [...root.querySelectorAll('[data-rp-home-count]')].map(node => ({
      node,
      target: Number(node.dataset.rpHomeCount) || 0,
      finalText: node.textContent
    }));
    const metrics = root.querySelector('.rp-home-hero__results');
    const metricsRect = metrics && metrics.getBoundingClientRect();
    const prepareCounts = canObserve && !reducedMotion.matches && root.dataset.rpHomeCounted !== 'true' && counts.length > 0 &&
      metricsRect && metricsRect.width > 0 && metricsRect.height > 0 && !isInViewport(metrics);
    let counted = !prepareCounts;
    let countStarted = false;
    let visible = isInViewport(root);
    let disposed = false;
    let countFrame = 0;
    let parallaxFrame = 0;
    let parallaxValue = 0;
    let parallaxTime = 0;
    let lastParallax = '';
    let countObserver;
    let heroObserver;
    if (counted) root.dataset.rpHomeCounted = 'true';

    function restoreCounts() {
      if (countFrame) cancelAnimationFrame(countFrame);
      countFrame = 0;
      counted = true;
      root.dataset.rpHomeCounted = 'true';
      counts.forEach(item => {
        if (item.node.textContent !== item.finalText) item.node.textContent = item.finalText;
      });
      root.classList.remove('is-metrics-prepared', 'is-metrics-visible');
      if (countObserver) countObserver.disconnect();
    }

    function animateCounts() {
      if (disposed || counted || countStarted || reducedMotion.matches || document.hidden) return;
      countStarted = true;
      root.classList.add('is-metrics-visible');
      if (countObserver) countObserver.disconnect();
      let start;
      function tick(now) {
        countFrame = 0;
        if (disposed || reducedMotion.matches || document.hidden) {
          restoreCounts();
          return;
        }
        if (start === undefined) start = now;
        const progress = Math.min(1, (now - start) / 800);
        const eased = 1 - Math.pow(1 - progress, 3);
        counts.forEach(item => {
          const value = String(Math.round(item.target * eased));
          if (item.node.textContent !== value) item.node.textContent = value;
        });
        if (progress < 1) countFrame = requestAnimationFrame(tick);
        else restoreCounts();
      }
      countFrame = requestAnimationFrame(tick);
    }

    function canMove() {
      return canObserve && !disposed && visible && !document.hidden && !reducedMotion.matches;
    }

    function stopParallax(reset = false) {
      if (parallaxFrame) cancelAnimationFrame(parallaxFrame);
      parallaxFrame = 0;
      parallaxTime = 0;
      root.classList.remove('is-parallax-active');
      if (reset) {
        parallaxValue = 0;
        lastParallax = '';
        root.style.removeProperty('--rp-home-parallax');
      }
    }

    function updateParallax(now) {
      parallaxFrame = 0;
      if (!canMove()) {
        stopParallax();
        return;
      }
      const rect = root.getBoundingClientRect();
      const progress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      const target = Math.max(-16, Math.min(16, (progress - .5) * 32));
      const elapsed = Math.min(64, parallaxTime ? now - parallaxTime : 16.7);
      parallaxTime = now;
      parallaxValue += (target - parallaxValue) * (1 - Math.exp(-elapsed / 85));
      if (Math.abs(target - parallaxValue) < .05) parallaxValue = target;
      const value = `${parallaxValue.toFixed(1)}px`;
      if (lastParallax !== value) {
        root.style.setProperty('--rp-home-parallax', value);
        lastParallax = value;
      }
      if (parallaxValue !== target) parallaxFrame = requestAnimationFrame(updateParallax);
      else parallaxTime = 0;
    }

    function onScroll() {
      if (!canMove()) {
        stopParallax();
        return;
      }
      root.classList.add('is-parallax-active');
      if (!parallaxFrame) parallaxFrame = requestAnimationFrame(updateParallax);
    }

    function onVisibilityChange() {
      if (document.hidden) {
        stopParallax();
        if (countStarted && !counted) restoreCounts();
      } else {
        visible = isInViewport(root);
        onScroll();
        if (!counted && metrics && isInViewport(metrics)) animateCounts();
      }
    }

    function onMotionChange() {
      if (reducedMotion.matches) {
        stopParallax(true);
        restoreCounts();
      } else {
        visible = isInViewport(root);
        onScroll();
      }
    }

    function dispose() {
      if (disposed) return;
      disposed = true;
      stopParallax(true);
      restoreCounts();
      if (heroObserver) heroObserver.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (reducedMotion.removeEventListener) reducedMotion.removeEventListener('change', onMotionChange);
      else reducedMotion.removeListener(onMotionChange);
      delete root.dataset.rpHomeReady;
      heroes.delete(root);
    }

    heroes.set(root, dispose);
    if (prepareCounts) {
      counts.forEach(item => { item.node.textContent = '0'; });
      root.classList.add('is-metrics-prepared');
      countObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio > 0) animateCounts();
        });
      }, { threshold: .01 });
      countObserver.observe(metrics);
    }
    if (canObserve) {
      heroObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          visible = entry.isIntersecting;
          if (visible) onScroll();
          else stopParallax();
        });
      }, { threshold: 0 });
      heroObserver.observe(root);
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      document.addEventListener('visibilitychange', onVisibilityChange);
      if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', onMotionChange);
      else reducedMotion.addListener(onMotionChange);
      onScroll();
    }
  }

  function initAll(scope = document) {
    if (scope.matches && scope.matches('[data-rp-home-hero]')) initHero(scope);
    scope.querySelectorAll('[data-rp-home-hero]').forEach(initHero);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    heroes.forEach((dispose, root) => {
      if (event.target === root || event.target.contains(root)) dispose();
    });
  });
  window.addEventListener('pagehide', () => {
    heroes.forEach(dispose => dispose());
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) initAll();
  });
})();
